import { VALIDATION, WEBINAR_API } from "../constants";
import { SessionFormState } from "./sessionFormState";
import {
  deriveStartAt,
  isSessionDateNotPast,
  isSessionStartAllowed,
} from "./deriveStartAt";

export type ChecklistItem = {
  key: string;
  label: string;
  ok: boolean;
};

export type PublishValidation = {
  titleOk: boolean;
  oneOk: boolean;
  fullOk: boolean;
  takeOk: boolean;
  topicOk: boolean;
  hostOk: boolean;
  dateOk: boolean;
  timeOk: boolean;
  whenOk: boolean;
  minsOk: boolean;
  recEndOk: boolean;
  accessOk: boolean;
  urlOk: boolean;
  clashOk: boolean;
  isValid: boolean;
  checklist: ChecklistItem[];
};

const JOIN_URL_RE = /^(https?:\/\/\S+|[a-z0-9][a-z0-9._/-]{2,})$/i;

export function isJoinUrlOk(provider: string, joinUrl: string): boolean {
  const url = (joinUrl || "").trim();
  if (!provider) return false;
  if (url.length < VALIDATION.joinUrlMin) return false;
  return JOIN_URL_RE.test(url);
}

export function isRecEndOk(form: SessionFormState): boolean {
  if (!form.recurrence || form.recurrence === "none") return true;
  if (!form.recEnd?.trim() || !form.date?.trim()) return false;
  return form.recEnd.trim() >= form.date.trim();
}

export function validateDraft(form: SessionFormState): {
  ok: boolean;
  message?: string;
} {
  if (!form.title.trim()) {
    return { ok: false, message: "Add a title before saving as draft" };
  }
  if (form.date.trim() && !isSessionDateNotPast(form.date)) {
    return { ok: false, message: "Pick a date that is today or later" };
  }
  if (
    form.date.trim() &&
    form.time.trim() &&
    !isSessionStartAllowed(form.date, form.time)
  ) {
    return {
      ok: false,
      message: `Start must be at least ${VALIDATION.startMinHours} hours from now`,
    };
  }
  if (!isRecEndOk(form)) {
    return {
      ok: false,
      message: "Series end date must be on or after the session date",
    };
  }
  return { ok: true };
}

/** Per-step required fields before Next while creating / editing a session. */
export function validateSessionStep(
  form: SessionFormState,
  step: number,
): { ok: boolean; message?: string } {
  const v = validatePublish(form);

  if (step <= 0) {
    if (!v.titleOk) {
      return {
        ok: false,
        message: `Title must be ${VALIDATION.titleMin}–${VALIDATION.titleMax} characters`,
      };
    }
    if (!v.oneOk) {
      return {
        ok: false,
        message: "Add a one-line description that does not repeat the title",
      };
    }
    if (!v.topicOk) return { ok: false, message: "Select a topic" };
    if (!v.hostOk) return { ok: false, message: "Select a host" };
    return { ok: true };
  }

  if (step === 1) {
    if (!v.fullOk) {
      return {
        ok: false,
        message: `Full description must be ${VALIDATION.fullDescMin}–${VALIDATION.fullDescMax} characters (no links)`,
      };
    }
    if (!v.takeOk) {
      return {
        ok: false,
        message: `Add ${VALIDATION.takeawayMin}–${VALIDATION.takeawayMax} takeaways`,
      };
    }
    return { ok: true };
  }

  if (step === 2) {
    if (!v.accessOk) {
      return { ok: false, message: "Choose who can access this session" };
    }
    return { ok: true };
  }

  // Schedule step (publish path / future Next-from-schedule use).
  if (!v.whenOk || !v.minsOk) {
    return {
      ok: false,
      message: `Pick a valid date/time (≥ ${VALIDATION.startMinHours}h from now) and duration`,
    };
  }
  if (!v.recEndOk) {
    return {
      ok: false,
      message: "Series end date must be on or after the session date",
    };
  }
  if (!form.format?.trim()) {
    return { ok: false, message: "Select a format" };
  }
  if (!form.lang?.trim()) {
    return { ok: false, message: "Select a language" };
  }
  if (!v.urlOk) {
    return {
      ok: false,
      message: "Add a streaming provider and a valid join URL / room id",
    };
  }
  if (!v.clashOk) {
    return {
      ok: false,
      message: "Host already has a session in this time window",
    };
  }
  return { ok: true };
}

export function validatePublish(
  form: SessionFormState,
  sessions: Array<{
    id: string;
    hostId: string;
    status: string;
    backendStatus?: string;
    start: number;
  }> = [],
): PublishValidation {
  const title = form.title.trim();
  const one = form.oneLiner.trim();
  const full = form.fullDesc.trim();
  const takes = form.takeaways.map((t) => t.trim()).filter(Boolean);
  const mins = Number(form.mins);

  const titleOk =
    title.length >= VALIDATION.titleMin && title.length <= VALIDATION.titleMax;
  const oneOk =
    one.length > 0 &&
    one.length <= VALIDATION.oneLinerMax &&
    one.toLowerCase() !== title.toLowerCase();
  const fullOk =
    full.length >= VALIDATION.fullDescMin &&
    full.length <= VALIDATION.fullDescMax &&
    !/https?:\/\//i.test(full);
  const takeOk =
    takes.length >= VALIDATION.takeawayMin &&
    takes.length <= VALIDATION.takeawayMax &&
    takes.every((x) => x.length <= VALIDATION.takeawayCharMax);
  const topicOk = !!form.topicId;
  const hostOk = !!form.hostId;
  const dateOk = isSessionDateNotPast(form.date);
  const timeOk =
    !!form.time.trim() && isSessionStartAllowed(form.date, form.time);
  const whenOk = dateOk && timeOk;
  const minsOk =
    mins >= VALIDATION.minsMin &&
    mins <= VALIDATION.minsMax &&
    mins % VALIDATION.minsStep === 0;
  const recEndOk = isRecEndOk(form);
  const accessOk =
    form.access === "open" ||
    (form.access === "members" &&
      (WEBINAR_API.canUseCohorts ? form.cohorts.length > 0 : true));
  const urlOk = isJoinUrlOk(form.provider, form.joinUrl);
  const clashOk = !hasHostClash(form, sessions);

  const checklist: ChecklistItem[] = [
    {
      key: "title",
      label: "Title, 8–70 characters in sentence case",
      ok: titleOk,
    },
    {
      key: "one",
      label: "One-line description that does not repeat the title",
      ok: oneOk,
    },
    {
      key: "full",
      label: "Full description, 40–600 characters",
      ok: fullOk,
    },
    {
      key: "take",
      label: "Three to five takeaways",
      ok: takeOk,
    },
    {
      key: "topicHost",
      label: "Topic and host selected",
      ok: topicOk && hostOk,
    },
    {
      key: "when",
      label: `Date/time at least ${VALIDATION.startMinHours}h from now · duration 15–120 min`,
      ok: whenOk && minsOk,
    },
    {
      key: "recurrence",
      label: "Series end on or after the session date (if repeating)",
      ok: recEndOk,
    },
    {
      key: "access",
      label:
        form.access === "members" && WEBINAR_API.canUseCohorts
          ? "Cohorts chosen for a members-only session"
          : "Access set",
      ok: accessOk,
    },
    {
      key: "url",
      label: "Streaming provider and a valid join URL / room id",
      ok: urlOk,
    },
    {
      key: "clash",
      label: `Host free within ${VALIDATION.hostClashMinutes} minutes of this slot`,
      ok: clashOk,
    },
  ];

  const isValid = checklist.every((c) => c.ok);

  return {
    titleOk,
    oneOk,
    fullOk,
    takeOk,
    topicOk,
    hostOk,
    dateOk,
    timeOk,
    whenOk,
    minsOk,
    recEndOk,
    accessOk,
    urlOk,
    clashOk,
    isValid,
    checklist,
  };
}

export function hasHostClash(
  form: SessionFormState,
  sessions: Array<{
    id: string;
    hostId: string;
    status: string;
    backendStatus?: string;
    start: number;
  }>,
): boolean {
  if (!form.hostId) return false;
  const { start } = deriveStartAt(form.date, form.time);
  if (!Number.isFinite(start)) return false;
  const windowMs = VALIDATION.hostClashMinutes * 60000;
  return sessions.some((s) => {
    const status = s.backendStatus || s.status;
    return (
      s.id !== form.id &&
      s.hostId === form.hostId &&
      status === "scheduled" &&
      Math.abs(s.start - start) < windowMs
    );
  });
}
